import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Check, Layers } from "lucide-react";
import api from "../api";
import { useCart } from "../contexts/CartContext";
import ProductImage from "./ProductImage";

// Stacks + bundle % come from the server (single source of truth with checkout pricing).
let stacksCache = null;
export { loadProducts };
export const loadStacks = () => {
  if (!stacksCache) stacksCache = api.get("/research-stacks").then(({ data }) => data).catch(() => ({ stacks: [] }));
  return stacksCache;
};

let productsCache = null;
const loadProducts = () => {
  if (!productsCache) productsCache = api.get("/products").then(({ data }) => (Array.isArray(data) ? data : data.products || [])).catch(() => []);
  return productsCache;
};

function StackCard({ stack, products, pct, complete }) {
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);
  const total = products.reduce((s, p) => s + Number(p.variants[0].price || 0), 0);
  const stackPrice = complete ? total * (1 - pct / 100) : total;
  const addAll = () => {
    products.forEach((p) => addItem(p, p.variants[0], 1));
    setAdded(true);
    setTimeout(() => setAdded(false), 2200);
  };
  const key = stack.name.toLowerCase().replace(/[^a-z]+/g, "-");

  return (
    <div data-testid={`stack-${key}`} className="bg-[#0C0C12] p-6 flex flex-col">
      <div className="flex items-center justify-between gap-3">
        <Link to={stack.slug ? `/stacks/${stack.slug}` : "/stacks"} data-testid={`stack-${key}-link`} className="font-heading text-lg hover:text-[#00C3FF] transition-colors">{stack.name}</Link>
        {complete && pct > 0 && (
          <span data-testid={`stack-${key}-save`} className="px-2 py-1 border border-[#00C3FF]/50 text-[#00C3FF] font-mono-tech text-[9px] uppercase tracking-[0.18em] shrink-0">Save {pct}%</span>
        )}
      </div>
      <div className="flex items-center gap-2 mt-4">
        {products.map((p, i) => (
          <React.Fragment key={p.slug}>
            {i > 0 && <Plus size={12} className="text-[#5C657A] shrink-0" />}
            <Link to={`/product/${p.slug}`} data-testid={`stack-${key}-item-${p.slug}`} className="flex-1 min-w-0 text-center group">
              <div className="vial-bg aspect-square flex items-center justify-center">
                <ProductImage product={p} className="w-[75%] h-[80%] transition-transform group-hover:scale-105" />
              </div>
              <div className="font-mono-tech text-[10px] uppercase tracking-wider mt-2 truncate">{p.name}</div>
            </Link>
          </React.Fragment>
        ))}
      </div>
      <div className="flex-1" />
      <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/5">
        <div className="flex items-baseline gap-2">
          <span data-testid={`stack-${key}-price`} className="font-mono-tech text-sm text-[#00C3FF]">${stackPrice.toFixed(2)}</span>
          {complete && pct > 0 && <span className="font-mono-tech text-[10px] text-[#5C657A] line-through">${total.toFixed(2)}</span>}
        </div>
        <button data-testid={`stack-${key}-add`} onClick={addAll} className="btn-primary text-[10px]" style={{ padding: "10px 16px" }}>
          {added ? <><Check size={12} /> Added</> : <><Plus size={12} /> Add Stack</>}
        </button>
      </div>
    </div>
  );
}

export default function ResearchStacks({ currentSlug = null }) {
  const [all, setAll] = useState([]);
  const [cfg, setCfg] = useState({ stacks: [] });
  useEffect(() => { loadProducts().then(setAll); loadStacks().then(setCfg); }, []);
  const bySlug = Object.fromEntries(all.map((p) => [p.slug, p]));
  const stacks = cfg.stacks
    .filter((s) => !currentSlug || s.slugs.includes(currentSlug))
    .map((s) => {
      const products = s.slugs.map((x) => bySlug[x]).filter((p) => p && p.variants?.length);
      return { stack: s, products, complete: products.length === s.slugs.length };
    })
    .filter((s) => s.products.length >= 2);
  if (!stacks.length) return null;

  return (
    <section data-testid="research-stacks" className={currentSlug ? "mt-16" : "max-w-7xl mx-auto px-6 py-20"}>
      <div className="overline flex items-center gap-2"><Layers size={12} /> RESEARCH STACKS</div>
      <h2 className="font-heading text-2xl sm:text-3xl mt-3 mb-8">Compounds commonly researched together.</h2>
      <div className={`grid grid-cols-1 ${currentSlug ? "md:grid-cols-2" : "md:grid-cols-3"} gap-px bg-white/5`}>
        {stacks.map(({ stack, products, complete }) => <StackCard key={stack.id || stack.name} stack={stack} products={products} pct={Number(stack.percent_off || 0)} complete={complete} />)}
      </div>
      <p className="font-mono-tech text-[10px] text-[#5C657A] mt-4 uppercase tracking-wider">Stack price applies automatically at checkout when every compound in the stack is in your cart (1–5 vials each). For research use only.</p>
    </section>
  );
}
